import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FileText, Upload } from "lucide-react";
import { z } from "zod";

import {
  useUpdateWorkerProfile,
  useUploadEligibilityDoc,
} from "@/features/workers/model/useWorkerProfile";
import { splitName } from "@/features/onboarding/ui/IdentityStep";
import {
  LEGAL_STATUS_LABELS,
  type LegalStatus,
  type WorkerProfile,
} from "@/features/workers/types";
import { formatPhoneInput, nationalPhoneDigits } from "@/shared/lib/phone";
import {
  ALLOWED_DOC_TYPES,
  DOC_ACCEPT_ATTR,
  validateUpload,
} from "@/shared/lib/uploadValidation";

const todayIso = () => new Date().toISOString().slice(0, 10);

const SIN_DIGITS_RE = /^\d{9}$/;
const stripNonDigits = (v: string) => v.replace(/\D/g, "").slice(0, 9);

const schema = z
  .object({
    first_name: z.string().min(1, "First name is required").max(100),
    last_name: z.string().min(1, "Last name is required").max(100),
    // Optional in profile-edit (leave blank to keep current). When typed,
    // must be 10 NANP digits so the backend can store E.164 and SMS
    // notifications actually deliver.
    phone: z
      .string()
      .optional()
      .refine(
        (v) => v == null || v === "" || nationalPhoneDigits(v).length === 10,
        { message: "Enter a 10-digit phone number" },
      ),
    city: z.string().max(100).optional(),
    bio: z.string().max(1000).optional(),
    legal_status: z
      .enum(["CITIZEN", "PERMANENT_RESIDENT", "OPEN_WORK_PERMIT"])
      .optional(),
    work_permit_expires_on: z.string().optional(),
    // Optional in profile-edit mode — user can leave blank to keep the value
    // already on file. When non-empty, must be 9 digits.
    sin: z
      .string()
      .optional()
      .refine(
        (v) => v == null || v === "" || SIN_DIGITS_RE.test(stripNonDigits(v)),
        { message: "Enter all 9 digits of your SIN." },
      ),
  })
  .superRefine((values, ctx) => {
    if (values.legal_status === "OPEN_WORK_PERMIT") {
      if (!values.work_permit_expires_on) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["work_permit_expires_on"],
          message: "Permit expiry date is required.",
        });
      } else if (values.work_permit_expires_on <= todayIso()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["work_permit_expires_on"],
          message: "Permit expiry must be in the future.",
        });
      }
    }
  });

type FormValues = z.infer<typeof schema>;

interface Props {
  profile: WorkerProfile;
}

const LEGAL_OPTIONS: LegalStatus[] = ["CITIZEN", "PERMANENT_RESIDENT", "OPEN_WORK_PERMIT"];

export function WorkerProfileForm({ profile }: Props) {
  const update = useUpdateWorkerProfile();
  const uploadDoc = useUploadEligibilityDoc();
  const docInputRef = useRef<HTMLInputElement>(null);
  const [docError, setDocError] = useState<string | null>(null);

  const seeded = splitName(profile.name ?? "");
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: seeded.first,
      last_name: seeded.last,
      city: profile.city ?? "",
      bio: profile.bio ?? "",
      phone: "",
      legal_status: profile.legal_status ?? undefined,
      work_permit_expires_on: profile.work_permit_expires_on ?? "",
      sin: "",
    },
  });

  useEffect(() => {
    const next = splitName(profile.name ?? "");
    reset({
      first_name: next.first,
      last_name: next.last,
      city: profile.city ?? "",
      bio: profile.bio ?? "",
      phone: "",
      legal_status: profile.legal_status ?? undefined,
      work_permit_expires_on: profile.work_permit_expires_on ?? "",
      sin: "",
    });
  }, [
    profile.id,
    profile.name,
    profile.city,
    profile.bio,
    profile.legal_status,
    profile.work_permit_expires_on,
    reset,
  ]);

  const selectedLegal = watch("legal_status");

  const onDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validateUpload(file, ALLOWED_DOC_TYPES);
    if (err) {
      setDocError(err);
      return;
    }
    setDocError(null);
    uploadDoc.mutate(file);
  };

  const onSubmit = handleSubmit((values) => {
    const sinDigits = values.sin ? stripNonDigits(values.sin) : "";
    const fullName = `${values.first_name.trim()} ${values.last_name.trim()}`.trim();
    const phoneE164 = values.phone ? `+1${nationalPhoneDigits(values.phone)}` : undefined;
    update.mutate({
      name: fullName,
      city: values.city || undefined,
      bio: values.bio || undefined,
      phone: phoneE164,
      legal_status: values.legal_status,
      // Only send SIN when the user actually typed one in this session — empty
      // means "keep what's on file."
      sin: sinDigits ? sinDigits : undefined,
      work_permit_expires_on:
        values.legal_status === "OPEN_WORK_PERMIT"
          ? values.work_permit_expires_on || null
          : null,
    });
  });

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label htmlFor="first_name" className="text-sm font-medium">
            First name
          </label>
          <input
            id="first_name"
            {...register("first_name")}
            autoComplete="given-name"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.first_name && (
            <p className="text-xs text-destructive">{errors.first_name.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <label htmlFor="last_name" className="text-sm font-medium">
            Last name
          </label>
          <input
            id="last_name"
            {...register("last_name")}
            autoComplete="family-name"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.last_name && (
            <p className="text-xs text-destructive">{errors.last_name.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <label htmlFor="phone" className="text-sm font-medium">
          Phone
        </label>
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <input
              id="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+1 (613) 555-1234"
              value={field.value ?? ""}
              onChange={(e) => field.onChange(formatPhoneInput(e.target.value))}
              onBlur={field.onBlur}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          )}
        />
        {errors.phone && (
          <p className="text-xs text-destructive">{errors.phone.message}</p>
        )}
        <p className="text-xs text-copy-muted">
          We send shift confirmations and reminders by SMS.
        </p>
      </div>

      <div className="space-y-1">
        <label htmlFor="city" className="text-sm font-medium">
          City
        </label>
        <input
          id="city"
          {...register("city")}
          placeholder="Ottawa"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="bio" className="text-sm font-medium">
          About
        </label>
        <textarea
          id="bio"
          rows={3}
          {...register("bio")}
          placeholder="Short bio — experience, what shifts you're looking for, etc."
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="space-y-2 rounded-md border border-border bg-surface-sunken p-4">
        <h3 className="text-sm font-semibold text-copy">Work eligibility</h3>
        <p className="text-xs text-copy-muted">
          Updates here re-enter the verification queue if anything material changes.
        </p>

        <div className="space-y-1">
          <label className="text-sm font-medium text-copy">Legal status</label>
          <select
            {...register("legal_status")}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Select…</option>
            {LEGAL_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {LEGAL_STATUS_LABELS[opt]}
              </option>
            ))}
          </select>
        </div>

        {selectedLegal === "OPEN_WORK_PERMIT" && (
          <div className="space-y-1">
            <label
              htmlFor="work_permit_expires_on"
              className="text-sm font-medium text-copy"
            >
              Permit expiry date
            </label>
            <input
              id="work_permit_expires_on"
              type="date"
              min={todayIso()}
              {...register("work_permit_expires_on")}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.work_permit_expires_on && (
              <p className="text-xs text-destructive">
                {errors.work_permit_expires_on.message}
              </p>
            )}
          </div>
        )}

        <div className="space-y-1">
          <label htmlFor="sin" className="text-sm font-medium text-copy">
            Social Insurance Number (SIN)
          </label>
          <Controller
            name="sin"
            control={control}
            render={({ field }) => (
              <input
                id="sin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                maxLength={9}
                placeholder={
                  profile.sin_provided
                    ? "On file — leave blank to keep"
                    : "9 digits"
                }
                value={field.value ?? ""}
                onChange={(e) => {
                  setValue("sin", stripNonDigits(e.target.value), {
                    shouldDirty: true,
                  });
                }}
                onBlur={field.onBlur}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm tracking-wider tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
              />
            )}
          />
          {errors.sin && (
            <p className="text-xs text-destructive">{errors.sin.message}</p>
          )}
          <p className="text-xs text-copy-muted">
            Encrypted at rest and never displayed back — not even the last four
            digits.
            {profile.sin_provided
              ? " A SIN is already on file; leave blank to keep it or type a new value to replace."
              : ""}
          </p>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-copy">
            ID document
          </label>
          <p className="text-xs text-copy-muted">
            PR card, citizenship certificate, or work permit. PDF or image, ≤5 MB.
          </p>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-surface text-copy-subtle">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              {profile.eligibility_document_url ? (
                <a
                  href={profile.eligibility_document_url}
                  target="_blank"
                  rel="noreferrer"
                  className="block truncate text-sm font-medium text-primary hover:underline"
                  title={profile.eligibility_document_url}
                >
                  Uploaded — view
                </a>
              ) : (
                <p className="text-sm text-copy-muted">No document uploaded yet</p>
              )}
              {profile.eligibility_document_uploaded_at && (
                <p className="text-xs text-copy-subtle">
                  Uploaded{" "}
                  {new Date(profile.eligibility_document_uploaded_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => docInputRef.current?.click()}
              disabled={uploadDoc.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-sunken disabled:opacity-50"
            >
              <Upload className="h-4 w-4" />
              {uploadDoc.isPending
                ? "Uploading…"
                : profile.eligibility_document_url
                  ? "Replace"
                  : "Upload"}
            </button>
            <input
              ref={docInputRef}
              type="file"
              accept={DOC_ACCEPT_ATTR}
              onChange={onDocChange}
              className="hidden"
            />
          </div>
          {docError && <p className="text-xs text-destructive-foreground">{docError}</p>}
          {uploadDoc.isError && (
            <p className="text-xs text-destructive-foreground">
              {(uploadDoc.error as { response?: { data?: { detail?: string } } })
                ?.response?.data?.detail ?? "Upload failed."}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={!isDirty || update.isPending}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          {update.isPending ? "Saving..." : "Save"}
        </button>
        {update.isSuccess && !isDirty && (
          <span className="text-xs text-copy-muted">Saved</span>
        )}
        {update.isError && (
          <span className="text-xs text-destructive">
            {(update.error as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail ?? "Could not save changes."}
          </span>
        )}
      </div>
    </form>
  );
}
